export function isDependentSelect(optn) {
  return optn.dependsOnOptionIndex !== undefined;
}

export function hasSelectAnswer(value) {
  return value !== undefined && value !== null && value !== "";
}

export const CLONE_HIDDEN_ANSWER_BEHAVIORS = {
  clear: "clear",
  preserve: "preserve",
};

function getSelectGroupsFromQuestion(question) {
  if (!question || !Array.isArray(question.questions)) return [];

  if (question.type === "select") {
    return question.questions.filter((selectQuestion) =>
      Array.isArray(selectQuestion.options)
    );
  }

  if (question.type === "text") {
    return [
      {
        options: question.questions,
      },
    ];
  }

  return [];
}

function getDependentSelectSource(question, optn) {
  if (optn.cloneFromDbqName) return null;
  if (!isDependentSelect(optn)) return null;
  return question.options[optn.dependsOnOptionIndex];
}

export function getSelectOptionValues(question, optn) {
  const values = optn.values || [];
  const source = getDependentSelectSource(question, optn);

  if (!source) return values;
  if (!hasSelectAnswer(source.answer)) return [];

  if (optn.valueFilter === "belowSelected") {
    const selectedValue = Number(source.answer);
    return values.filter((value) => Number(value) < selectedValue);
  }

  return values;
}

function isDependentSelectDisabled(question, optn) {
  const source = getDependentSelectSource(question, optn);
  return !!source && !hasSelectAnswer(source.answer);
}

export function isSelectDisabled(question, optn) {
  return !!optn.disabled || isDependentSelectDisabled(question, optn);
}

export function getSelectValue(question, optn) {
  if (optn.cloneFromDbqName) return optn.answer;
  if (!isDependentSelect(optn)) return optn.answer;

  const availableValues = getSelectOptionValues(question, optn);
  return availableValues.map(String).indexOf(String(optn.answer)) !== -1
    ? optn.answer
    : "";
}

export function getSelectPlaceholder(question, optn) {
  if (isDependentSelectDisabled(question, optn)) {
    return optn.disabledPlaceholder || "Select previous value first";
  }

  return optn.placeholder || "Select";
}

export function clearInvalidDependentSelectAnswers(selectQuestion, sourceIndex) {
  if (!selectQuestion || !Array.isArray(selectQuestion.options)) {
    return;
  }

  const pendingSourceIndexes =
    sourceIndex === undefined ? [undefined] : [sourceIndex];

  while (pendingSourceIndexes.length > 0) {
    const currentSourceIndex = pendingSourceIndexes.shift();

    selectQuestion.options.forEach((option, index) => {
      if (!isDependentSelect(option)) {
        return;
      }

      if (
        currentSourceIndex !== undefined &&
        option.dependsOnOptionIndex !== currentSourceIndex
      ) {
        return;
      }

      const availableValues = getSelectOptionValues(selectQuestion, option);
      if (
        hasSelectAnswer(option.answer) &&
        availableValues.map(String).indexOf(String(option.answer)) === -1
      ) {
        option.answer = "";
        pendingSourceIndexes.push(index);
      }
    });
  }
}

function clearDefaultSelectAnswerOnce(option) {
  if (!option || !option.clearDefaultOnNormalize) {
    return;
  }

  if (option.defaultClearedOnNormalize) {
    return;
  }

  if (option.answer === option.defaultVal) {
    option.answer = "";
  }
  option.defaultClearedOnNormalize = true;
}

export function normalizeSelectDependencies(questions) {
  if (!questions || !questions.length) return questions;

  questions.forEach((question) => {
    getSelectGroupsFromQuestion(question).forEach((selectQuestion) => {
      selectQuestion.options.forEach(clearDefaultSelectAnswerOnce);
      clearInvalidDependentSelectAnswers(selectQuestion);
    });
  });

  return questions;
}

function getQuestionByRadioGroup(questions, radioGroup) {
  if (!questions || !radioGroup) return null;
  return questions.find((question) => question.radioGroup === radioGroup);
}

function getSelectOptionByDbqName(questions, dbqName) {
  if (!questions || !dbqName) return null;

  for (let question of questions) {
    for (let selectQuestion of getSelectGroupsFromQuestion(question)) {
      if (!selectQuestion.options) continue;

      const option = selectQuestion.options.find(
        (optn) => optn.dbqName === dbqName
      );
      if (option) return option;
    }
  }

  return null;
}

function shouldCloneSelectQuestion(questions, question) {
  if (!question.cloneWhenRadioGroup) return true;

  const sourceQuestion = getQuestionByRadioGroup(
    questions,
    question.cloneWhenRadioGroup
  );
  return !!(
    sourceQuestion &&
    sourceQuestion.answer === (question.cloneWhenAnswer || "Yes")
  );
}

function getCloneHiddenAnswerBehavior(question) {
  if (question.cloneAnswerBehaviorOnHidden) {
    return question.cloneAnswerBehaviorOnHidden;
  }

  if (question.preserveCloneAnswersWhenHidden) {
    return CLONE_HIDDEN_ANSWER_BEHAVIORS.preserve;
  }

  return CLONE_HIDDEN_ANSWER_BEHAVIORS.clear;
}

function shouldPreserveCloneAnswerWhenHidden(question) {
  return (
    getCloneHiddenAnswerBehavior(question) ===
    CLONE_HIDDEN_ANSWER_BEHAVIORS.preserve
  );
}

function hasPreservedCloneAnswer(question, optn) {
  return !!(
    question.preservedCloneAnswersWhenHidden &&
    Object.prototype.hasOwnProperty.call(
      question.preservedCloneAnswersWhenHidden,
      optn.dbqName
    )
  );
}

function preserveCloneAnswerBeforeOverwrite(question, optn) {
  if (!shouldPreserveCloneAnswerWhenHidden(question) || !optn.dbqName) return;
  if (hasPreservedCloneAnswer(question, optn)) return;

  question.preservedCloneAnswersWhenHidden =
    question.preservedCloneAnswersWhenHidden || {};
  question.preservedCloneAnswersWhenHidden[optn.dbqName] = optn.answer;
}

function restorePreservedCloneAnswer(question, optn) {
  if (!shouldPreserveCloneAnswerWhenHidden(question)) {
    optn.answer = "";
    return;
  }

  if (hasPreservedCloneAnswer(question, optn)) {
    optn.answer = question.preservedCloneAnswersWhenHidden[optn.dbqName];
  }
}

export function syncClonedSelectAnswers(questions) {
  if (!questions || !questions.length) return questions;

  questions.forEach((question) => {
    const selectGroups = getSelectGroupsFromQuestion(question);
    if (!selectGroups.length) return;

    const shouldClone = shouldCloneSelectQuestion(questions, question);
    if (question.cloneWhenRadioGroup) {
      question.show = shouldClone;
    }

    selectGroups.forEach((selectQuestion) => {
      if (!selectQuestion.options) return;

      selectQuestion.options.forEach((optn) => {
        if (!optn.cloneFromDbqName) return;

        if (!shouldClone) {
          restorePreservedCloneAnswer(question, optn);
          return;
        }

        preserveCloneAnswerBeforeOverwrite(question, optn);
        const sourceOption = getSelectOptionByDbqName(
          questions,
          optn.cloneFromDbqName
        );
        optn.answer =
          sourceOption && hasSelectAnswer(sourceOption.answer)
            ? sourceOption.answer
            : "";
      });
    });
  });

  return questions;
}

export function isCloneOnlySelectQuestion(question) {
  if (!question || question.type !== "select" || !question.questions) {
    return false;
  }

  return question.questions.every((selectQuestion) => {
    return (
      selectQuestion.options &&
      selectQuestion.options.every((optn) => !!optn.cloneFromDbqName)
    );
  });
}
